import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Container from "@/components/ui/container";
import Breadcrumbs from "@/components/ui/breadcrumbs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import WhatsAppFloat from "@/components/ui/whatsapp-float";
import SEO from "@/components/SEO";
import { Users, Target, Award, MapPin, Clock, Building2, Briefcase, Star, Brain } from "lucide-react";
import RelatedLinks from "@/components/ui/related-links";
import { motion } from "framer-motion";
import { AuroraHero } from "@/components/ui/aurora-hero";

const About = () => {
  // Removido - substituído por seção de história narrativa

  const team = [
    {
      icon: <Award className="w-8 h-8" />,
      title: "Profissionais Certificados",
      description: "Equipe com certificações oficiais TOTVS Fluig",
      stat: "100%"
    },
    {
      icon: <Briefcase className="w-8 h-8" />,
      title: "Experiência Comprovada",
      description: "Mais de 300.000 horas em projetos Fluig",
      stat: "+300k"
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "Empresas Atendidas",
      description: "Clientes de diversos segmentos e portes",
      stat: "20+"
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: "Taxa de Sucesso",
      description: "Projetos entregues com excelência",
      stat: "98%"
    }
  ];

  const offices = [
    {
      city: "São Paulo",
      region: "Sudeste",
      description: "Sede principal e centro de operações"
    },
    {
      city: "Rio de Janeiro",
      region: "Sudeste",
      description: "Atendimento região metropolitana"
    },
    {
      city: "Belo Horizonte",
      region: "Sudeste",
      description: "Cobertura Minas Gerais e interior"
    },
    {
      city: "Brasília",
      region: "Centro-Oeste",
      description: "Proximidade ao governo federal"
    }
  ];

  const differentials = [
    {
      title: "100% Foco Fluig",
      description: "Especialização exclusiva na plataforma TOTVS Fluig, garantindo expertise máxima",
      icon: <Target className="w-6 h-6" />
    },
    {
      title: "Integração Nativa TOTVS",
      description: "Conhecimento profundo do ecossistema TOTVS para integrações perfeitas",
      icon: <Building2 className="w-6 h-6" />
    },
    {
      title: "Equipe Certificada",
      description: "Profissionais com certificações oficiais e experiência comprovada",
      icon: <Award className="w-6 h-6" />
    },
    {
      title: "Atendimento Nacional",
      description: "Atendimento especializado em Olinda/PE com alcance nacional",
      icon: <MapPin className="w-6 h-6" />
    },
    {
      title: "Metodologia Ágil",
      description: "Entregas rápidas com metodologia comprovada em +100 projetos",
      icon: <Clock className="w-6 h-6" />
    },
    {
      title: "Suporte Contínuo",
      description: "Acompanhamento pós-implementação para garantir o sucesso",
      icon: <Users className="w-6 h-6" />
    },
    {
      title: "Inovação com IA",
      description: "Consultoria especializada em IA para otimizar processos e potencializar resultados",
      icon: <Brain className="w-6 h-6" />
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO 
        title="Sobre a Future Station - Especialistas Fluig com +300k Horas"
        description="Conheça a Future Station: 300.000+ horas em projetos Fluig, presença em 4 capitais, equipe 100% certificada TOTVS. Especialistas em automação de processos empresariais."
        keywords={['sobre future station', 'empresa fluig', 'consultoria totvs', 'equipe certificada fluig', 'especialistas bpm', 'história future station']}
        type="website"
      />
      <Header />

      <main>
        {/* Hero Section */}
        <section id="hero">
          <AuroraHero>
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 fs-gradient-text">
                Sobre a Future Station
              </h1>
              <p className="text-xl text-muted-foreground dark:text-white/80 max-w-3xl mx-auto leading-relaxed">
                Especialistas em transformação digital com mais de 300.000 horas de experiência
                em projetos Fluig. Presente em 4 capitais brasileiras, somos referência em
                automação de processos empresariais.
              </p>
            </div>
          </AuroraHero>
        </section>

        {/* Nossa História */}
        <section className="py-20 bg-background dark:bg-fs-dark-950 relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-fs-cyan-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-fs-magenta-500/10 rounded-full blur-3xl" />
          </div>

          <Container className="py-20 relative z-10">
            <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                Nossa História
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Uma jornada de inovação e especialização em transformação digital
              </p>
            </motion.div>

            {/* História em layout mais criativo */}
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Card principal da história */}
              <Card className="lg:col-span-2 bg-gradient-to-br from-fs-purple-500/5 to-fs-cyan-500/5 dark:from-fs-purple-500/10 dark:to-fs-cyan-500/10 bg-card/80 backdrop-blur-sm border-border hover:border-fs-purple-500/40 transition-all duration-300 shadow-md dark:shadow-none">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-fs-purple-500 to-fs-magenta-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-3">Nossa Fundação</h3>
                      <p className="text-foreground/80 leading-relaxed mb-4">
                        A Future Station nasceu com o propósito de ser a referência absoluta em soluções TOTVS Fluig no Brasil. Desde nossa fundação, construímos uma trajetória sólida baseada em especialização técnica, inovação constante e resultados excepcionais.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Card de números */}
              <Card className="bg-gradient-to-br from-fs-magenta-500/5 to-fs-cyan-500/5 dark:from-fs-magenta-500/10 dark:to-fs-cyan-500/10 bg-card/80 backdrop-blur-sm border-border shadow-md dark:shadow-none">
                <CardContent className="p-8 text-center">
                  <div className="text-4xl font-bold fs-gradient-text mb-2">300k+</div>
                  <p className="text-fs-magenta-500 font-semibold mb-2">Horas de Experiência</p>
                  <p className="text-muted-foreground text-sm">Projetos Fluig entregues com excelência</p>
                  
                  <div className="mt-6 pt-6 border-t border-border">
                    <div className="text-2xl font-bold text-foreground mb-1">98%</div>
                    <p className="text-muted-foreground text-sm">Taxa de Sucesso</p>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Expansão e IA */}
            <div className="grid lg:grid-cols-2 gap-8 mt-8">
              <Card className="bg-gradient-to-br from-fs-cyan-500/5 to-fs-purple-500/5 dark:from-fs-cyan-500/10 dark:to-fs-purple-500/10 bg-card/80 backdrop-blur-sm border-border shadow-md dark:shadow-none">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-fs-cyan-500 to-fs-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-3">Expansão Nacional</h3>
                      <p className="text-foreground/80 leading-relaxed">
                        Expandimos nossa presença para atender clientes em todo território nacional, desenvolvendo soluções especializadas e acumulando mais de 20 empresas atendidas.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-fs-magenta-500/5 to-fs-purple-500/5 dark:from-fs-magenta-500/10 dark:to-fs-purple-500/10 bg-card/80 backdrop-blur-sm border-border shadow-md dark:shadow-none">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-fs-magenta-500 to-fs-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-3">Inovação com IA</h3>
                      <p className="text-foreground/80 leading-relaxed">
                        Ampliamos nosso portfólio com consultoria em Inteligência Artificial, integrando IA ao ecossistema TOTVS para potencializar ainda mais os resultados dos nossos clientes.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          </Container>
        </section>

        {/* Missão, Visão e Valores */}
        <Container className="py-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="bg-gradient-to-br from-fs-purple-500/5 to-fs-magenta-500/5 dark:from-fs-purple-500/10 dark:to-fs-magenta-500/10 bg-card/80 backdrop-blur-sm border-border hover:border-fs-purple-500/40 transition-all duration-300 shadow-md dark:shadow-none">
                <CardContent className="p-8 text-center">
                  <Target className="w-12 h-12 text-fs-purple-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold fs-gradient-text mb-4">Missão</h3>
                  <p className="text-muted-foreground">
                    Impulsionar empresas a alcançarem eficiência, agilidade e controle por meio de soluções especializadas no TOTVS Fluig, integrando automação de processos, otimização de fluxos e capacitação de equipes. Complementamos essa expertise com Inteligência Artificial aplicada, potencializando resultados e abrindo novas possibilidades de inovação.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-fs-cyan-500/5 to-fs-purple-500/5 dark:from-fs-cyan-500/10 dark:to-fs-purple-500/10 bg-card/80 backdrop-blur-sm border-border hover:border-fs-cyan-500/40 transition-all duration-300 shadow-md dark:shadow-none">
                <CardContent className="p-8 text-center">
                  <Users className="w-12 h-12 text-fs-cyan-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold fs-gradient-text mb-4">Visão</h3>
                  <p className="text-muted-foreground">
                    Ser reconhecida como a principal referência nacional em TOTVS Fluig, entregando projetos de alta performance e impacto real, e evoluir para liderar também a aplicação estratégica de IA no ecossistema TOTVS e fora dele, transformando tecnologia em crescimento sustentável para nossos clientes.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-fs-magenta-500/5 to-fs-cyan-500/5 dark:from-fs-magenta-500/10 dark:to-fs-cyan-500/10 bg-card/80 backdrop-blur-sm border-border hover:border-fs-magenta-500/40 transition-all duration-300 shadow-md dark:shadow-none">
                <CardContent className="p-8 text-center">
                  <Award className="w-12 h-12 text-fs-magenta-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold fs-gradient-text mb-4">Valores</h3>
                  <p className="text-muted-foreground">
                    Inovação, excelência técnica, transparência e foco total 
                    no sucesso do cliente.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </Container>

        {/* Equipe e Números */}
        <section className="py-20 bg-background dark:bg-fs-dark-950 relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-fs-purple-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-fs-cyan-500/10 rounded-full blur-3xl" />
          </div>

          <Container className="py-20 relative z-10">
            <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                Nossa Equipe
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Profissionais certificados e especializados em entregar 
                soluções de excelência
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {team.map((member, index) => (
                <Card key={index} className="bg-card/80 backdrop-blur-sm border border-border hover:border-fs-purple-500/40 transition-all duration-300 text-center shadow-md dark:shadow-none">
                  <CardContent className="p-6">
                    <div className="text-fs-purple-500 mb-4 flex justify-center">
                      {member.icon}
                    </div>
                    <div className="text-3xl font-bold fs-gradient-text mb-2">
                      {member.stat}
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {member.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {member.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          </Container>
        </section>

        {/* Diferenciais Competitivos */}
        <Container className="py-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                Por que Future Station?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Diferenciais que nos tornam únicos no mercado de soluções Fluig
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {differentials.map((diff, index) => (
                <Card key={index} className="bg-card/80 backdrop-blur-sm border border-border hover:border-fs-purple-500/40 transition-all duration-300 group shadow-md dark:shadow-none">
                  <CardContent className="p-6">
                    <div className="text-fs-purple-500 mb-4 group-hover:text-fs-cyan-500 transition-colors">
                      {diff.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      {diff.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {diff.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </Container>

        {/* Presença Nacional */}
        <Container className="py-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                Presença Nacional
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Atendimento especializado com alcance nacional para estar 
                sempre perto dos nossos clientes
              </p>
            </div>

            {/* Mapa 3D do Brasil + Cards das Capitais */}
            <div className="grid lg:grid-cols-5 gap-8 items-center py-12">
              {/* Mapa 3D do Brasil - 60% */}
              <div className="lg:col-span-3 flex justify-center">
                <div className="relative group">
                  {/* Gradiente sutil de fundo */}
                  <div className="absolute inset-0 bg-gradient-to-br from-fs-purple-500/5 to-fs-cyan-500/5 rounded-2xl blur-3xl scale-125 opacity-60"></div>
                  
                  {/* Mapa 3D do Brasil - Imagem Principal */}
                  <div className="relative">
                    <img 
                      src="/assets/mapa-brasil.png"
                      alt="Mapa 3D do Brasil com cobertura nacional Future Station" 
                      className="w-[28rem] h-[28rem] md:w-[32rem] md:h-[32rem] lg:w-[36rem] lg:h-[36rem] object-contain 
                               filter drop-shadow-lg 
                               group-hover:drop-shadow-[0_0_40px_rgba(139,92,246,0.4)] 
                               transform group-hover:scale-[1.02] 
                               transition-all duration-700 ease-out
                               animate-[breath_4s_ease-in-out_infinite]"
                    />
                  </div>
                </div>
              </div>

              {/* Cards dos Estados - 40% */}
              <div className="lg:col-span-2 space-y-4">
                {/* São Paulo */}
                <Card className="bg-gradient-to-br from-fs-purple-500/5 to-fs-magenta-500/5 dark:from-fs-purple-500/10 dark:to-fs-magenta-500/10 bg-card/80 backdrop-blur-sm border-border hover:border-fs-purple-500/40 transition-all duration-300 group shadow-md dark:shadow-none">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-fs-purple-500 to-fs-magenta-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">SP</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-foreground">São Paulo</h3>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Pernambuco */}
                <Card className="bg-gradient-to-br from-fs-cyan-500/5 to-fs-purple-500/5 dark:from-fs-cyan-500/10 dark:to-fs-purple-500/10 bg-card/80 backdrop-blur-sm border-border hover:border-fs-cyan-500/40 transition-all duration-300 group shadow-md dark:shadow-none">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-fs-cyan-500 to-fs-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">PE</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-foreground">Pernambuco</h3>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Rio Grande do Sul */}
                <Card className="bg-gradient-to-br from-fs-magenta-500/5 to-fs-cyan-500/5 dark:from-fs-magenta-500/10 dark:to-fs-cyan-500/10 bg-card/80 backdrop-blur-sm border-border hover:border-fs-magenta-500/40 transition-all duration-300 group shadow-md dark:shadow-none">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-fs-magenta-500 to-fs-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">RS</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-foreground">Rio Grande do Sul</h3>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Goiás */}
                <Card className="bg-gradient-to-br from-fs-purple-500/5 to-fs-cyan-500/5 dark:from-fs-purple-500/10 dark:to-fs-cyan-500/10 bg-card/80 backdrop-blur-sm border-border hover:border-fs-purple-500/40 transition-all duration-300 group shadow-md dark:shadow-none">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-fs-purple-500 to-fs-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">GO</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-foreground">Goiás</h3>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </Container>

        {/* Related Links */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/50">
          <div className="max-w-6xl mx-auto px-6">
            <RelatedLinks 
              title="Conheça Nossos Serviços Especializados"
              links={[
                {
                  title: "Consultoria Fluig Especializada",
                  description: "Maximize o potencial da sua plataforma Fluig com nossa consultoria especializada e 300.000+ horas de experiência.",
                  href: "/consultoria-fluig",
                  category: "Consultoria"
                },
                {
                  title: "Automação de Processos Empresariais",
                  description: "Transforme seus processos manuais com automação inteligente e workflows otimizados.",
                  href: "/automacao-processos",
                  category: "Automação"
                },
                {
                  title: "Serviços de TI Especializados",
                  description: "Outsourcing Fluig, desenvolvimento JavaScript/React e soluções Python com SLA garantido.",
                  href: "/servicos/ti",
                  category: "Serviços"
                },
                {
                  title: "Catálogo FluigApps",
                  description: "Descubra nossos 16 FluigApps prontos para uso e acelere sua implementação.",
                  href: "/flows",
                  category: "Soluções"
                },
                {
                  title: "Consultoria de IA",
                  description: "Potencialize seus processos com Inteligência Artificial integrada ao ecossistema TOTVS.",
                  href: "/consultoria-ia",
                  category: "Inovação"
                }
              ]}
            />
          </div>
        </section>

        {/* CTA Section */}
        <Container className="py-20">
          <div className="max-w-4xl mx-auto text-center">
            <Card className="bg-gradient-to-r from-fs-purple-500/10 via-fs-magenta-500/10 to-fs-cyan-500/10 border-fs-purple-500/20">
              <CardContent className="p-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                  Pronto para Transformar sua Empresa?
                </h2>
                <p className="text-lg text-muted-foreground dark:text-white/70 mb-8 max-w-2xl mx-auto">
                  Entre em contato conosco e descubra como mais de 300.000 horas de experiência
                  em Fluig podem revolucionar seus processos empresariais.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    variant="fs-primary" 
                    size="lg"
                    onClick={() => window.open('https://wa.me/5581981658439?text=Olá! Gostaria de conhecer mais sobre as soluções Future Station.', '_blank')}
                  >
                    Falar no WhatsApp
                  </Button>
                  <Button 
                    variant="fs-secondary" 
                    size="lg"
                    onClick={() => window.location.href = '/contato'}
                  >
                    Solicitar Proposta
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </Container>
      </main>
      
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default About;